import { Icon } from '@iconify/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Bell, BookOpen, HelpCircle, Megaphone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useLocalStorage } from 'usehooks-ts';
import { useSocket } from '../SocketProvider';
import { ChangelogPopover } from './ChangelogPopover';
import { RetrainStateEnum } from './types';
import { Endpoint } from '@/Config/api/endpoint';
import { httpClient } from '@/Config/api/httpClient.config';
import { SocketKey } from '@/Config/api/socketKey';
import { pushGtmEvent } from '@/Config/libs/analytics';
import { SONY_SCENARIO_ID_LIST } from '@/Config/scenarioIds';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { SidebarTrigger } from '@/Domain/components/Shared/Sidebar/Sidebar';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { AnimatedThemeToggler } from '@/Domain/components/Shared/ThemeSelect/AnimatedThemeToggler';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { RetrainState } from '@/Domain/components/TopBar/Retrain';
import { UpgradePlanButton } from '@/Domain/components/UpgradePlanButton/UpgradePlanButton';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useChangelog } from '@/Domain/hooks/Changelog/useChangelog.hook';
import { useSupportWidgetStore } from '@/Domain/store/supportWidget.store';

const LOCAL_STORAGE_ENTRY_NAME = '__qc_conversation';
const LOCAL_STORAGE_ENTRY_NAME_ENCRYPTED = `${LOCAL_STORAGE_ENTRY_NAME}_encrypted`;

export const TOP_BAR_HEIGHT = 53;
export const TopBar = () => {
  const { t } = useTranslation('topbar');
  const { socket } = useSocket();
  const {
    open: isWidgetOpen,
    toggleOpen,
    openWithMessage,
    feedbackPopoverOpen,
    setFeedbackPopoverOpen,
  } = useSupportWidgetStore();
  const [feedbackText, setFeedbackText] = useState('');
  const [retrainState, setRetrainState] = useState<RetrainStateEnum | null>(null);
  const [showUpToDateBanner, setShowUpToDateBanner] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const previousRetrainStateRef = useRef<RetrainStateEnum | null>(null);
  const { capture } = usePostHogAnalytics();
  const changelog = useChangelog();

  const { scenario } = useParams();
  const localStorageItemName = `${LOCAL_STORAGE_ENTRY_NAME_ENCRYPTED}_${scenario ?? ''}`;
  const [, setConversation, removeConversation] = useLocalStorage(localStorageItemName, null);

  const retrain = useQuery<KnowledgeBase>({ queryKey: ['knowledge_base'] });

  const handeDocumentationClick = () => window.open('https://docs.quickchat.ai/', '_blank');

  useEffect(() => {
    if (isWidgetOpen) {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          toggleOpen();
        }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
      };
    }
  }, [isWidgetOpen, toggleOpen]);

  useEffect(() => {
    const handleRetrainStateChanged = () => {
      retrain
        .refetch()
        .then((data) => {
          const newState =
            (data?.data?.retrain_state as RetrainStateEnum) || RetrainStateEnum.UP_TO_DATE;
          setRetrainState(newState);
        })
        .catch(console.error);
    };

    socket.on(SocketKey.knowledgeBase.retrainStateChanged, handleRetrainStateChanged);

    return () => {
      socket.off(SocketKey.knowledgeBase.retrainStateChanged, handleRetrainStateChanged);
    };
  }, [socket, retrain]);

  useEffect(() => {
    if (retrain.data) {
      setRetrainState(
        (retrain.data.retrain_state as RetrainStateEnum) || RetrainStateEnum.UP_TO_DATE,
      );
    }
  }, [retrain.data]);

  useEffect(() => {
    if (
      retrainState === RetrainStateEnum.UP_TO_DATE &&
      previousRetrainStateRef.current === RetrainStateEnum.IN_PROGRESS
    ) {
      pushGtmEvent(GtmEventEnum.KnowledgeBaseTrained, { scenario_id: scenario });
      capture(PostHogEventEnum.RetrainCompleted);
      setShowUpToDateBanner(true);
      setIsFadingOut(false);
      const fadeTimer = setTimeout(() => setIsFadingOut(true), 4500);
      const hideTimer = setTimeout(() => {
        setShowUpToDateBanner(false);
        setIsFadingOut(false);
      }, 5000);
      previousRetrainStateRef.current = retrainState;
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
    previousRetrainStateRef.current = retrainState;
  }, [retrainState, scenario, capture]);

  const retrainMutation = useMutation({
    mutationFn: async () => await httpClient.post(Endpoint.knowledgeBase.retrain),
    // Optimistic IN_PROGRESS so the click is never perceived as a no-op.
    onMutate: () => setRetrainState(RetrainStateEnum.IN_PROGRESS),
    onError: (error: AxiosError) => {
      // Forget the optimistic IN_PROGRESS so reconciliation can't fake a completed-retrain banner.
      previousRetrainStateRef.current = null;
      // Reconcile with server truth and surface the failure instead of reverting silently.
      void retrain
        .refetch()
        .then((data) =>
          setRetrainState(
            (data?.data?.retrain_state as RetrainStateEnum) || RetrainStateEnum.TO_BE_RETRAINED,
          ),
        );
      toast.error(
        error.response?.status === 409 ? t('retrain_already_running') : t('retrain_error'),
      );
    },
  });

  const handleRetrain = (): void => {
    capture(PostHogEventEnum.RetrainStarted);
    retrainMutation.mutate();
  };

  const previewGtmFired = useRef(false);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const messageType = event.data?.type;
      // The preview widget iframe posts these as the visitor starts or resets a conversation.
      if (messageType === 'quickchat_conversation') {
        const convId = event.data.data?.conv_id;
        if (convId) {
          setConversation(convId);
          if (!previewGtmFired.current) {
            previewGtmFired.current = true;
            pushGtmEvent(GtmEventEnum.PreviewConversationStarted, { scenario_id: scenario });
          }
        }
      }
      if (messageType === 'quickchat_reset') {
        removeConversation();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [setConversation, removeConversation, scenario]);

  return (
    <>
      <div
        className="flex h-13 shrink-0 items-center justify-between gap-2 border-b border-sidebar-border bg-sidebar pl-4 pr-4 md:pl-6"
        style={{ height: TOP_BAR_HEIGHT }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <SidebarTrigger className="md:hidden" />
          {!scenario || !SONY_SCENARIO_ID_LIST.includes(scenario) ? (
            <div className="min-w-0">
              {retrainState === RetrainStateEnum.IN_PROGRESS ||
              retrainState === RetrainStateEnum.TO_BE_RETRAINED ? (
                <RetrainState
                  retrainState={retrainState}
                  handleRetrain={handleRetrain}
                  isPending={retrainMutation.isPending}
                />
              ) : showUpToDateBanner ? (
                <div
                  className={`flex items-center gap-2 text-sm font-medium text-success transition-opacity duration-500 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
                >
                  <Icon icon="mingcute:check-circle-fill" className="flex-shrink-0" />
                  <p className="truncate text-success">{t('retrain_up_to_date')}</p>
                </div>
              ) : null}
            </div>
          ) : (
            <div />
          )}
        </div>
        <TooltipProvider>
          <div className="flex items-center gap-0.5">
            <UpgradePlanButton
              iconOnly
              size="xs"
              className="md:hidden !h-8 !w-8 rounded-xl"
              source="top_bar"
            />
            <UpgradePlanButton
              size="xs"
              className="hidden md:inline-flex rounded-xl !px-4 !h-8 !text-sm"
              source="top_bar"
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  className="ml-2 h-8 rounded-xl px-2 text-sm text-muted-foreground md:px-2.5"
                  onClick={toggleOpen}
                  aria-label={t('button.need_help')}
                >
                  <HelpCircle />
                  <span className="hidden md:inline">{t('button.need_help_mobile')}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="md:hidden">{t('button.need_help')}</TooltipContent>
            </Tooltip>
            <Popover open={feedbackPopoverOpen} onOpenChange={setFeedbackPopoverOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="xs"
                      className="hidden h-8 rounded-xl px-2 text-sm text-muted-foreground md:inline-flex md:px-2.5"
                      aria-label={t('button.feedback')}
                      data-testid="topbar-feedback-button"
                    >
                      <Megaphone />
                      <span className="hidden md:inline">{t('button.feedback_mobile')}</span>
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent className="md:hidden">{t('button.feedback')}</TooltipContent>
              </Tooltip>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="w-80 p-3"
                data-testid="topbar-feedback-popover"
                onOpenAutoFocus={(event) => {
                  // Radix's default focuses the first focusable element with the
                  // caret at position 0; for a re-opened popover with persisted
                  // draft text we want the caret at the end so users can keep typing.
                  event.preventDefault();
                  const textarea = (event.currentTarget as HTMLElement).querySelector('textarea');
                  if (textarea) {
                    textarea.focus();
                    const len = textarea.value.length;
                    textarea.setSelectionRange(len, len);
                  }
                }}
              >
                <div className="flex flex-col gap-3">
                  <AutosizeTextarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder={t('feedback_popover.placeholder')}
                    minHeight={116}
                    maxHeight={300}
                    className="!py-2 !px-3 text-sm"
                    data-testid="topbar-feedback-textarea"
                  />
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!feedbackText.trim()}
                    onClick={() => {
                      openWithMessage(`Feedback message: ${feedbackText.trim()}`);
                      setFeedbackText('');
                      setFeedbackPopoverOpen(false);
                    }}
                    data-testid="topbar-feedback-send"
                  >
                    {t('feedback_popover.send')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  className="h-8 rounded-xl px-2 text-sm text-muted-foreground md:px-2.5"
                  onClick={handeDocumentationClick}
                  aria-label={t('button.documentation')}
                >
                  <BookOpen />
                  <span className="hidden md:inline">{t('button.documentation_mobile')}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="md:hidden">{t('button.documentation')}</TooltipContent>
            </Tooltip>
            <Popover
              onOpenChange={(open) => {
                if (open) changelog.markAllSeen();
              }}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="xs"
                      className="relative h-8 rounded-xl px-2 text-sm text-muted-foreground md:px-2.5"
                      aria-label={t('button.changelog')}
                      data-testid="topbar-changelog-button"
                    >
                      <Bell />
                      {changelog.hasUnseen && (
                        <span
                          className="absolute right-1 top-1 size-2 rounded-full bg-info"
                          data-testid="topbar-changelog-unseen-dot"
                        />
                      )}
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>{t('button.changelog')}</TooltipContent>
              </Tooltip>
              <PopoverContent align="end" sideOffset={8} className="w-96 p-0">
                <ChangelogPopover
                  entries={changelog.entries}
                  isLoading={changelog.isLoading}
                  isError={changelog.isError}
                />
              </PopoverContent>
            </Popover>
            <AnimatedThemeToggler className="!size-8 !p-2" />
          </div>
        </TooltipProvider>
      </div>
    </>
  );
};
